import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'
import { useShallow } from 'zustand/react/shallow'

import type { RobotProgressEvent, RunStatsDto } from '@brickup/shared/dto'
import { MatchStatus } from '@brickup/shared/enums'
import { MATCHING } from '@brickup/shared/locales/de/matching'
import type {
  Property,
  PropertyListing,
  SearchMandateMatch,
} from '@brickup/shared/models'
import { robotJob } from './matching-robot'
import { apiClient } from '@/api/client'
import { STALE_MATCH_COUNT_KEY } from '@/api/query-keys'
import { useInvalidateMatchingQueries } from '@/hooks/use-invalidate-matching-queries'
import { useMutationWithInvalidation } from '@/hooks/use-mutation-with-invalidation'
import { useRobotWebSocketStore } from '@/stores/use-robot-websocket-store'

export type { Property, PropertyListing, SearchMandateMatch }

/** Whether the matching robot is on, whether a run is going, when it last ran
 *  and when it runs next. */
export const useRobotStatus = robotJob.useStatus
/** The robot's switch and a run over every mandate, as its settings row
 *  drives them. */
export const useRobotMutations = robotJob.useMutations

/** Whether a run of the robot is going, as its status says. */
function useRobotRunning(): boolean {
  return useRobotStatus().data?.running ?? false
}

export async function runRobotForAll(): Promise<RunStatsDto> {
  const response = await apiClient.post<RunStatsDto>(robotJob.routes.trigger)
  return response.data
}

export async function runRobotForMandate(
  mandateId: string,
): Promise<RunStatsDto> {
  const response = await apiClient.post<RunStatsDto>(
    `${robotJob.routes.trigger}/${mandateId}`,
  )
  return response.data
}

export async function updateMatchStatus(
  matchId: number,
  status: MatchStatus,
  notes?: string,
): Promise<void> {
  await apiClient.patch(`/matching/match/${matchId}/status`, { status, notes })
}

export async function getPropertyMatchCount(
  propertyId: number,
): Promise<{ count: number }> {
  const response = await apiClient.get<{ count: number }>(
    `/matching/property/${propertyId}/match-count`,
  )
  return response.data
}

export async function rejectPropertyForAllUsers(
  propertyId: number,
): Promise<{ count: number }> {
  const response = await apiClient.patch<{ count: number }>(
    `/matching/property/${propertyId}/reject-all`,
  )
  return response.data
}

export async function deleteAllMatchesForMandate(
  mandateId: string,
): Promise<{ deleted: number }> {
  const response = await apiClient.delete<{ deleted: number }>(
    `/matching/mandate/${mandateId}/matches`,
  )
  return response.data
}

export async function clearHubSpotCache(): Promise<{ message: string }> {
  const response = await apiClient.delete<{ message: string }>('/hubspot/cache')
  return response.data
}

/**
 * Starts the robot for one mandate or for all of them.
 *
 * Nothing is invalidated on success: the run reports itself over the socket —
 * `robot:started` sets the running state, `robot:mandate:complete` writes the
 * mandate straight into the cache, `robot:completed` invalidates what a global
 * run touched.
 */
export function useRunRobot() {
  return useMutationWithInvalidation(
    (mandateId?: string) =>
      mandateId ? runRobotForMandate(mandateId) : runRobotForAll(),
    [],
    { errorFallback: MATCHING.robot.startFailed },
  )
}

export function useUpdateMatchStatus() {
  const { invalidateAfterMatchUpdate } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    ({
      matchId,
      status,
      notes,
    }: {
      matchId: number
      status: MatchStatus
      notes?: string
    }) => updateMatchStatus(matchId, status, notes),
    invalidateAfterMatchUpdate,
  )
}

export function usePropertyMatchCount(propertyId: number | null) {
  return useQuery({
    queryKey: ['property-match-count', propertyId],
    queryFn: () =>
      propertyId
        ? getPropertyMatchCount(propertyId)
        : Promise.resolve({ count: 0 }),
    enabled: !!propertyId,
  })
}

export function useRejectPropertyForAllUsers() {
  const { invalidateAfterMatchUpdate } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    ({ propertyId }: { propertyId: number }) =>
      rejectPropertyForAllUsers(propertyId),
    invalidateAfterMatchUpdate,
  )
}

export function useDeleteAllMatchesForMandate() {
  const { invalidateAfterMatchUpdate } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    (mandateId: string) => deleteAllMatchesForMandate(mandateId),
    invalidateAfterMatchUpdate,
  )
}

export async function updateCustomerFeedback(
  matchId: number,
  likedByCustomer: boolean | null,
  customerFeedback?: string,
): Promise<void> {
  await apiClient.patch(`/matching/match/${matchId}/customer-feedback`, {
    likedByCustomer,
    customerFeedback,
  })
}

export async function createManualMatch(
  hubspotMandateId: string,
  propertyId: number,
): Promise<{ matchId: number; alreadyExists: boolean }> {
  const response = await apiClient.post<{
    matchId: number
    alreadyExists: boolean
  }>('/matching/match', { hubspotMandateId, propertyId })
  return response.data
}

export function useUpdateCustomerFeedback() {
  const { invalidateSearchMandates, invalidateCustomerMandates } =
    useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    ({
      matchId,
      likedByCustomer,
      customerFeedback,
    }: {
      matchId: number
      likedByCustomer: boolean | null
      customerFeedback?: string
    }) => updateCustomerFeedback(matchId, likedByCustomer, customerFeedback),
    () => {
      invalidateSearchMandates()
      invalidateCustomerMandates()
    },
  )
}

export function useCreateManualMatch() {
  const { invalidateAfterMatchUpdate } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    ({
      hubspotMandateId,
      propertyId,
    }: {
      hubspotMandateId: string
      propertyId: number
    }) => createManualMatch(hubspotMandateId, propertyId),
    invalidateAfterMatchUpdate,
  )
}

export async function acknowledgeFeedback(matchId: number): Promise<void> {
  await apiClient.patch(`/matching/match/${matchId}/acknowledge-feedback`)
}

export function useAcknowledgeFeedback() {
  const { invalidateAfterMatchUpdate } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    ({ matchId }: { matchId: number }) => acknowledgeFeedback(matchId),
    invalidateAfterMatchUpdate,
  )
}

export async function getStaleMatchCount(): Promise<{ count: number }> {
  const response = await apiClient.get<{ count: number }>(
    '/matching/stale-matches/count',
  )
  return response.data
}

export async function deleteStaleMatches(): Promise<{ deleted: number }> {
  const response = await apiClient.delete<{ deleted: number }>(
    '/matching/stale-matches',
  )
  return response.data
}

export function useStaleMatchCount() {
  return useQuery({
    queryKey: STALE_MATCH_COUNT_KEY,
    queryFn: getStaleMatchCount,
    staleTime: 5 * 60 * 1000,
  })
}

export function useDeleteStaleMatches() {
  const queryClient = useQueryClient()
  const { invalidateSearchMandates } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(deleteStaleMatches, () => {
    queryClient.invalidateQueries({ queryKey: STALE_MATCH_COUNT_KEY })
    invalidateSearchMandates()
  })
}

export function useClearHubSpotCache() {
  const { invalidateSearchMandates } = useInvalidateMatchingQueries()

  return useMutationWithInvalidation(
    () => clearHubSpotCache(),
    invalidateSearchMandates,
  )
}

/** Whether the run in `progress` is on this mandate now or has done it. */
function isOnMandate(
  progress: RobotProgressEvent | null,
  mandateId: string,
): boolean {
  return (
    progress?.currentMandate?.id === mandateId ||
    (progress?.completedMandateIds?.includes(mandateId) ?? false)
  )
}

/**
 * Whether a run is working on this mandate — either it is the one in hand, or
 * it has already been done in this run.
 *
 * Derived inside the selector on purpose: `robotProgress` is a fresh object on
 * every progress event, so a screen reading it re-renders once per mandate of
 * a run — a list of matches rebuilt a hundred times to grey out two buttons.
 * A boolean compares by value and changes twice.
 */
export function useIsRobotOnMandate(mandateId: string): boolean {
  const running = useRobotRunning()
  const onIt = useRobotWebSocketStore(state =>
    isOnMandate(state.robotProgress, mandateId),
  )
  return running && onIt
}

/**
 * "Is the robot on this mandate?", as a stable predicate.
 *
 * A list asks it once per row, so it must not be a fresh function on every
 * progress event: the mandate ids are read out of the event as strings, which
 * compare by value, and the predicate changes only when the set of them does.
 */
export function useIsRobotBusy(): (mandateId: string) => boolean {
  const running = useRobotRunning()
  const { currentMandateId, completedKey } = useRobotWebSocketStore(
    useShallow(state => ({
      currentMandateId: state.robotProgress?.currentMandate?.id,
      completedKey: state.robotProgress?.completedMandateIds?.join(',') ?? '',
    })),
  )

  const completed = useMemo(
    () => new Set(completedKey ? completedKey.split(',') : []),
    [completedKey],
  )

  return useCallback(
    (mandateId: string) =>
      running && (currentMandateId === mandateId || completed.has(mandateId)),
    [running, currentMandateId, completed],
  )
}

/** Where one mandate stands in the run, as three booleans. */
export function useRobotMandateState(mandateId: string, enabled: boolean) {
  const running = useRobotRunning()
  const { current, isDone, totalMandates } = useRobotWebSocketStore(
    useShallow(state => ({
      current: state.robotProgress?.currentMandate?.id === mandateId,
      isDone:
        state.robotProgress?.completedMandateIds?.includes(mandateId) ?? false,
      totalMandates: state.robotProgress?.totalMandates ?? 0,
    })),
  )
  const isProcessing = running && current
  return {
    isProcessing,
    isDone,
    // Only a run over every mandate has a queue; a single-mandate run leaves
    // the others alone, so nothing waits on it.
    isQueued:
      running && !isProcessing && !isDone && enabled && totalMandates > 1,
  }
}

/** The robot's socket and the progress of the run it reports. */
export function useRobotWebSocket() {
  return useRobotWebSocketStore(
    useShallow(state => ({
      isConnected: state.isConnected,
      robotProgress: state.robotProgress,
    })),
  )
}
